import { Keyword } from './firebaseRecords/keyword';
import { LanguageUppercase } from './languages';
import { TreeData, TreeDataNode } from './treeDatas';

export interface AutoTocSheetRecord {
    id: string;
    isPublished: boolean;
    keyword: Pick<Keyword, 'path' | 'treeId'>;
    number: number;
    title: string;
}

export interface AutoTocSection {
    id: string;
    items: AutoTocEntry[];
    path: string[];
    title: string;
    type: 'section';
    typography: string;
}

export interface AutoTocSheetLeaf {
    id: string;
    isPublished: boolean;
    label: string;
    number: number;
    path: string[];
    title: string;
    type: 'sheet';
}

export type AutoTocEntry = AutoTocSection | AutoTocSheetLeaf;

export const AUTO_TOC_NODE_ID = '__auto_toc__';

export const getAutoTocTitle = (language: LanguageUppercase) =>
    language === LanguageUppercase.FR ? 'Sommaire' : 'Contents';

interface BuildAutoTocArgs {
    dummyLevel?: boolean;
    includeUnpublished?: boolean;
    language: LanguageUppercase;
    sheets: AutoTocSheetRecord[];
    treeData: TreeData;
}

const getPathKey = (path: string[]) => path.join('/');

const buildSheetLabel = (number: number, title: string) => `${number}. ${title}`;

const buildSheetLeaf = (sheet: AutoTocSheetRecord): AutoTocSheetLeaf => ({
    id: sheet.id,
    isPublished: sheet.isPublished,
    label: buildSheetLabel(sheet.number, sheet.title),
    number: sheet.number,
    path: sheet.keyword.path,
    title: sheet.title,
    type: 'sheet',
});

const buildNodeEntries = (
    node: TreeDataNode,
    parentPath: string[],
    treeData: TreeData,
    language: LanguageUppercase,
    dummyLevel: boolean,
    sheetsByPath: Map<string, AutoTocSheetRecord[]>,
): AutoTocEntry[] => {
    const path = [...parentPath, node.id];
    const items: AutoTocEntry[] = [
        ...(sheetsByPath.get(getPathKey(path)) ?? []).map(buildSheetLeaf),
        ...(
            node.children?.flatMap((child) =>
                buildNodeEntries(child, path, treeData, language, dummyLevel, sheetsByPath),
            ) ?? []
        ),
    ];

    if (!items.length) {
        return [];
    }

    if (!node.title) {
        return items;
    }

    return [
        {
            id: node.id,
            items,
            path,
            title: node.title,
            type: 'section',
            typography: treeData.getLocalNodeTypography(path, language, dummyLevel),
        },
    ];
};

export const buildAutoToc = ({
    dummyLevel = false,
    includeUnpublished = false,
    language,
    sheets,
    treeData,
}: BuildAutoTocArgs): AutoTocEntry[] => {
    const sheetsByPath = sheets
        .filter(({ isPublished }) => includeUnpublished || isPublished)
        .sort((sheetA, sheetB) => sheetA.number - sheetB.number)
        .reduce((acc, sheet) => {
            const pathKey = getPathKey(sheet.keyword.path);
            const pathSheets = acc.get(pathKey) ?? [];

            pathSheets.push(sheet);
            acc.set(pathKey, pathSheets);

            return acc;
        }, new Map<string, AutoTocSheetRecord[]>());

    return treeData.content.flatMap((node) =>
        buildNodeEntries(node, [], treeData, language, dummyLevel, sheetsByPath),
    );
};
